import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, ArrowLeft, DollarSign, TrendingUp, Calendar } from 'lucide-react';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const AdminRevenue = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRevenue();
  }, []);

  const loadRevenue = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard-stats`, {
        headers: { 'admin-id': user.id }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading revenue:', error);
      toast.error('Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center"><p className="text-white">Loading...</p></div>;
  }

  const monthlyRevenue = stats?.monthly_revenue || 0;
  const totalClients = stats?.total_clients || 0;
  const avgRevenuePerClient = totalClients > 0 ? (monthlyRevenue / totalClients) : 0;

  return (
    <div className="min-h-screen bg-[#0f0f0f]">
      <nav className="bg-[#1C1917] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-2 hover:bg-white/5 rounded-full transition-colors">
              <ArrowLeft size={20} className="text-white/60" />
            </button>
            <Heart className="text-[#881337]" size={28} fill="#881337" />
            <span className="text-xl font-serif font-bold text-white">VowFlow</span>
            <span className="text-xs bg-[#881337] text-white px-2 py-0.5 rounded-full ml-2">ADMIN</span>
          </div>
        </div>
      </nav>

      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-serif text-white mb-2">Revenue Overview</h1>
          <p className="text-white/50">Track subscription revenue and growth</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-green-600 to-emerald-700 rounded-2xl p-8 text-white">
            <DollarSign size={32} className="opacity-80 mb-4" />
            <p className="text-5xl font-serif font-light mb-2">${monthlyRevenue.toFixed(0)}</p>
            <p className="text-sm opacity-90">Monthly Revenue</p>
          </div>

          <div className="bg-[#1C1917] border border-white/10 rounded-2xl p-8 text-white">
            <TrendingUp size={32} className="text-green-400 mb-4" />
            <p className="text-5xl font-serif font-light mb-2">${avgRevenuePerClient.toFixed(2)}</p>
            <p className="text-sm text-white/50">Avg per Client</p>
          </div>

          <div className="bg-[#1C1917] border border-white/10 rounded-2xl p-8 text-white">
            <Calendar size={32} className="text-blue-400 mb-4" />
            <p className="text-5xl font-serif font-light mb-2">{totalClients}</p>
            <p className="text-sm text-white/50">Active Subscriptions</p>
          </div>
        </div>

        <div className="bg-[#1C1917] rounded-2xl p-8 border border-white/10">
          <h2 className="text-xl font-serif text-white mb-6">Revenue Breakdown</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-4 border-b border-white/10">
              <div>
                <p className="text-white font-medium">Essentials Tier ($9.99/month)</p>
                <p className="text-white/50 text-sm">Standard features</p>
              </div>
              <p className="text-2xl font-serif text-white">-</p>
            </div>
            <div className="flex items-center justify-between py-4 border-b border-white/10">
              <div>
                <p className="text-white font-medium">Premium Tier ($14.99/month)</p>
                <p className="text-white/50 text-sm">All features + AI tools</p>
              </div>
              <p className="text-2xl font-serif text-white">{totalClients}</p>
            </div>
            <div className="flex items-center justify-between py-4 pt-6">
              <p className="text-white font-medium text-lg">Total Monthly Recurring Revenue</p>
              <p className="text-3xl font-serif text-green-400">${monthlyRevenue.toFixed(0)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminRevenue;